import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { recordActivity } from "../../utils/recordActivity";
import {
  beginSmsEnrollmentSession,
  confirmEnrollmentSms,
  endSmsEnrollmentSession,
  enrolledPhone,
  phoneEnrolled,
  reauthenticateForSms,
  sendEnrollmentSms,
  smsErrorMessage,
  unenrollSms,
} from "../../utils/firebaseSms";

const RESEND_WAIT_SECONDS = 30;

const ProfileSms: React.FC = () => {
  const toast = useToast();
  const { currentUser } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [phone, setPhone] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [turningOff, setTurningOff] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [smsDraft, setSmsDraft] = useState(false);
  const [savedPhone, setSavedPhone] = useState("");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textSecondary = useColorModeValue("gray.500", "gray.400");

  useEffect(() => {
    setEnabled(phoneEnrolled(currentUser));
    if (!currentUser) return;
    const status = httpsCallable<void, { smsPhone?: string }>(functions, "adminAccountStatus");
    status()
      .then((result) => {
        if (result.data.smsPhone) setSavedPhone(result.data.smsPhone);
      })
      .catch(() => undefined);
  }, [currentUser]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = window.setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  const send = async () => {
    if (!currentUser) return;
    if (!/^\+[1-9]\d{7,14}$/.test(phone.trim())) {
      toast({
        title: "Phone number",
        description: "Enter the phone number with the country code, for example +15145551234.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (!currentPassword) {
      toast({
        title: "Current password",
        description: "Enter your current password so the text code can be sent.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setSending(true);
    beginSmsEnrollmentSession();
    try {
      await reauthenticateForSms(currentUser, currentPassword);
      const signedIn = auth.currentUser;
      if (!signedIn) throw new Error("Sign in again, then turn on text message.");
      setVerificationId(await sendEnrollmentSms(auth, signedIn, phone.trim(), "super-admin-profile-recaptcha"));
      setCode("");
      setResendIn(RESEND_WAIT_SECONDS);
      toast({
        title: "Text message requested",
        description: "It can take a minute to arrive. You can send it again after 30 seconds.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      const code = error && typeof error === "object" && "code" in error ? String((error as { code?: string }).code) : "";
      if (code === "auth/wrong-password" || code === "auth/invalid-credential" || code === "auth/invalid-login-credentials") {
        endSmsEnrollmentSession();
      }
      toast({ title: "Text message", description: smsErrorMessage(error), status: "error", duration: 5000, isClosable: true });
    } finally {
      setSending(false);
    }
  };

  const confirm = async () => {
    if (!currentUser) return;
    setConfirming(true);
    try {
      const signedIn = auth.currentUser;
      if (!signedIn) throw new Error("Sign in again, then complete text message configuration.");
      await confirmEnrollmentSms(signedIn, verificationId, code);
      const remember = httpsCallable<{ phone: string }, { smsPhone: string }>(functions, "rememberAdminSmsPhone");
      await remember({ phone: phone.trim() });
      setSavedPhone(phone.trim());
      setEnabled(true);
      setSmsDraft(false);
      await recordActivity("profile_update", true, "Text message verification configured");
      endSmsEnrollmentSession();
      toast({
        title: "Text message verification enabled",
        description: "Sign in again. The next sign-in requires the text code.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      await signOut(auth);
    } catch (error) {
      toast({ title: "Text message", description: smsErrorMessage(error), status: "error", duration: 5000, isClosable: true });
    } finally {
      setConfirming(false);
    }
  };

  const turnOff = async () => {
    if (!currentUser) return;
    setTurningOff(true);
    try {
      const phoneNumber = enrolledPhone(currentUser) || savedPhone || phone.trim();
      if (phoneNumber) {
        const remember = httpsCallable<{ phone: string }, { smsPhone: string }>(functions, "rememberAdminSmsPhone");
        await remember({ phone: phoneNumber });
        setSavedPhone(phoneNumber);
      }
      await unenrollSms(currentUser);
      setEnabled(false);
      setSmsDraft(false);
      await recordActivity("profile_update", true, "Text message verification disabled");
      toast({
        title: "Text message verification disabled",
        description: "The mobile number remains on file.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({ title: "Text message", description: smsErrorMessage(error), status: "error", duration: 5000, isClosable: true });
    } finally {
      setTurningOff(false);
    }
  };

  const configured = enabled || Boolean(savedPhone);

  return (
    <Box
      bg={configured ? "green.50" : bgCard}
      borderRadius="lg"
      border="1px"
      borderColor={configured ? "green.300" : borderColor}
      boxShadow="sm"
      p={6}
    >
      <Text fontWeight="600" fontSize="md" mb={1}>Text message</Text>
      <Text fontSize="sm" color={textSecondary} mb={3}>
        {configured
          ? `Text message verification is configured for ${savedPhone || enrolledPhone(currentUser) || "the saved mobile number"}. When it is enabled, sign-in requires a text code after the password. When it is disabled, the text code is not required. The mobile number remains on file. Cellular service is required to receive the code.`
          : "Text message verification sends a code to a mobile number after the password. Cellular service is required. The method stays unused until it is configured and enabled."}
      </Text>
      <Text fontSize="sm" fontWeight="600" mb={4}>
        {configured ? "Configured" : "Not configured"}
        {configured ? ` · Sign-in requirement: ${enabled ? "Enabled" : "Disabled"}` : ""}
      </Text>
      {enabled && (
        <Button variant="outline" onClick={turnOff} isLoading={turningOff}>
          Disable
        </Button>
      )}
      {!enabled && configured && !smsDraft && (
        <Button
          onClick={() => {
            setPhone(savedPhone);
            setSmsDraft(true);
          }}
        >
          Enable
        </Button>
      )}
      {!configured && !smsDraft && (
        <Button onClick={() => setSmsDraft(true)}>Configure text message</Button>
      )}
      {smsDraft && !enabled && (
        <VStack align="stretch" spacing={4} mt={4}>
          <Box id="super-admin-profile-recaptcha" />
          <FormControl>
            <FormLabel>Current password</FormLabel>
            <Input
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Mobile number</FormLabel>
            <Input value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="+15145551234" />
          </FormControl>
          <Button
            onClick={send}
            isLoading={sending}
            isDisabled={Boolean(verificationId) && resendIn > 0}
            alignSelf="flex-start"
          >
            {!verificationId
              ? "Send verification code"
              : resendIn > 0
                ? `Send verification code again in ${resendIn}s`
                : "Send verification code again"}
          </Button>
          {verificationId && (
            <>
              <FormControl>
                <FormLabel>Verification code</FormLabel>
                <Input
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
              </FormControl>
              <Button onClick={confirm} isLoading={confirming} isDisabled={code.length < 6} alignSelf="flex-start">
                Complete configuration
              </Button>
            </>
          )}
          <Button variant="ghost" alignSelf="flex-start" onClick={() => setSmsDraft(false)}>
            Cancel
          </Button>
        </VStack>
      )}
    </Box>
  );
};

export default ProfileSms;
